import { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase/config";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../services/firebase/config";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Alert,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { createNewUser } from "../../services/firebase/userService";
import {
  generateTokenAndUrl,
  determineBaseUrl,
} from "../../services/nfc/nfcCardService";

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState("");
  const [userData, setUserData] = useState({
    name: "",
    idNumber: "",
    address: "",
    neighborhoodDollars: 1000,
    photoURL: "",
    nfcCardId: "",
    isAdmin: false,
  });

  // New user creation state
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    idNumber: "",
    address: "",
    neighborhoodDollars: 1000,
    isAdmin: false,
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [createUserError, setCreateUserError] = useState(null);
  const [createUserSuccess, setCreateUserSuccess] = useState(null);

  // Homes state for address dropdown
  const [homes, setHomes] = useState([]);

  // NFC token state
  const [generatedNfcToken, setGeneratedNfcToken] = useState(null);
  const [nfcTokenUrl, setNfcTokenUrl] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Delete user state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersCollection = collection(db, "users");
        const usersSnapshot = await getDocs(usersCollection);
        const usersList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort users alphabetically by email
        const sortedUsers = usersList.sort((a, b) => {
          const emailA = (a.email || "").toLowerCase();
          const emailB = (b.email || "").toLowerCase();
          return emailA.localeCompare(emailB);
        });

        setUsers(sortedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchHomes = async () => {
      try {
        const homesCollection = collection(db, "homes");
        const homesSnapshot = await getDocs(homesCollection);
        const homesList = homesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort homes alphabetically by address
        const sortedHomes = homesList.sort((a, b) => {
          const addressA = (a.address || "").toLowerCase();
          const addressB = (b.address || "").toLowerCase();
          return addressA.localeCompare(addressB);
        });

        setHomes(sortedHomes);
      } catch (error) {
        console.error("Error fetching homes:", error);
      }
    };

    fetchUsers();
    fetchHomes();
  }, []);

  const handleAdminToggle = async (userId, currentStatus) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        isAdmin: !currentStatus,
      });

      // Update users list while maintaining alphabetical sort
      const updatedUsers = users.map((user) => {
        if (user.id === userId) {
          return { ...user, isAdmin: !currentStatus };
        }
        return user;
      });

      setUsers(updatedUsers);
    } catch (error) {
      console.error("Error updating admin status:", error);
    }
  };

  const handleUserSelect = (userId) => {
    const user = users.find((u) => u.id === userId);
    setSelectedUser(userId);
    setUserData({
      name: user.name || "",
      idNumber: user.idNumber || "",
      address: user.address || "",
      neighborhoodDollars: user.neighborhoodDollars || 1000,
      photoURL: user.photoURL || "",
      nfcCardId: user.nfcCardId || "",
      isAdmin: user.isAdmin || false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const userRef = doc(db, "users", selectedUser);
      await updateDoc(userRef, userData);
      setUsers(
        users.map((user) => {
          if (user.id === selectedUser) {
            return { ...user, ...userData };
          }
          return user;
        })
      );
      alert("User profile updated successfully!");
    } catch (error) {
      alert("Error updating user profile: " + error.message);
    }
  };

  // Handle new user form changes
  const handleNewUserChange = (e) => {
    const { name, value, checked, type } = e.target;
    setNewUser({
      ...newUser,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle copying NFC URL to clipboard
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(nfcTokenUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000); // Reset after 3 seconds
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      alert("Failed to copy to clipboard");
    }
  };

  // Handle opening delete confirmation dialog
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Handle closing delete confirmation dialog
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  // Handle user deletion
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    try {
      // Delete user document from Firestore
      await deleteDoc(doc(db, "users", userToDelete.id));

      // Delete all NFC tokens associated with this user
      const nfcTokensQuery = query(
        collection(db, "nfcTokens"),
        where("userId", "==", userToDelete.id)
      );
      const nfcTokensSnapshot = await getDocs(nfcTokensQuery);
      const deletePromises = nfcTokensSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      // Remove user from local state
      setUsers(users.filter((u) => u.id !== userToDelete.id));

      // Close dialog
      setDeleteDialogOpen(false);
      setUserToDelete(null);

      alert("User deleted successfully!");
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user: " + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle new user creation
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserLoading(true);
    setCreateUserError(null);
    setCreateUserSuccess(null);
    setGeneratedNfcToken(null);
    setNfcTokenUrl(null);
    setCopySuccess(false);

    try {
      const createdUser = await createNewUser(newUser);
      const userId = createdUser.uid;

      setCreateUserSuccess("User created successfully!");

      // Generate NFC token for the new user
      try {
        const baseUrl = determineBaseUrl();
        const { token, nfcUrl } = await generateTokenAndUrl(
          userId,
          { email: newUser.email },
          baseUrl
        );
        setGeneratedNfcToken(token);
        setNfcTokenUrl(nfcUrl);
      } catch (nfcError) {
        console.error("Error generating NFC token:", nfcError);
        setCreateUserError(
          "User created, but failed to generate NFC token: " + nfcError.message
        );
      }

      // Reset form
      setNewUser({
        email: "",
        password: "",
        name: "",
        idNumber: "",
        address: "",
        neighborhoodDollars: 1000,
        isAdmin: false,
      });

      // Refresh users list
      const usersCollection = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort users alphabetically by email
      const sortedUsers = usersList.sort((a, b) => {
        const emailA = (a.email || "").toLowerCase();
        const emailB = (b.email || "").toLowerCase();
        return emailA.localeCompare(emailB);
      });

      setUsers(sortedUsers);
    } catch (error) {
      console.error("Error creating user:", error);
      setCreateUserError(error.message);
    } finally {
      setCreateUserLoading(false);
    }
  };

  // Export emails as CSV
  const handleExportEmails = () => {
    try {
      // Create CSV content
      const csvHeader =
        "Email,Name,Admin Status,ID Number,Address,Neighborhood Dollars\n";
      const csvContent = users
        .map((user) => {
          const email = user.email || "";
          const name = (user.name || "").replace(/,/g, ";"); // Replace commas to avoid CSV issues
          const adminStatus = user.isAdmin ? "Yes" : "No";
          const idNumber = (user.idNumber || "").replace(/,/g, ";");
          const address = (user.address || "").replace(/,/g, ";");
          const neighborhoodDollars = user.neighborhoodDollars || 0;

          return `"${email}","${name}","${adminStatus}","${idNumber}","${address}","${neighborhoodDollars}"`;
        })
        .join("\n");

      const fullCsv = csvHeader + csvContent;

      // Create and download the file
      const blob = new Blob([fullCsv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `user_emails_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting emails:", error);
      alert("Error exporting emails: " + error.message);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Create New User
        </Typography>

        {createUserError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {createUserError}
          </Alert>
        )}

        {createUserSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {createUserSuccess}
          </Alert>
        )}

        <form onSubmit={handleCreateUser}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={newUser.email}
                onChange={handleNewUserChange}
                required
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={newUser.password}
                onChange={handleNewUserChange}
                required
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={newUser.name}
                onChange={handleNewUserChange}
                required
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID Number"
                name="idNumber"
                value={newUser.idNumber}
                onChange={handleNewUserChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Address</InputLabel>
                <Select
                  name="address"
                  value={newUser.address}
                  label="Address"
                  onChange={handleNewUserChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {homes.map((home) => (
                    <MenuItem key={home.id} value={home.address}>
                      {home.address}
                      {home.status === "available" && (
                        <span
                          style={{
                            color: "#4caf50",
                            marginLeft: "8px",
                            fontSize: "0.85em",
                          }}
                        >
                          (Available)
                        </span>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Neighborhood Dollars"
                name="neighborhoodDollars"
                type="number"
                value={newUser.neighborhoodDollars}
                onChange={handleNewUserChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newUser.isAdmin}
                    onChange={handleNewUserChange}
                    name="isAdmin"
                  />
                }
                label="Admin User"
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={createUserLoading}
                sx={{ mt: 1 }}
              >
                {createUserLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  "Create User"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Display generated NFC token */}
        {generatedNfcToken && nfcTokenUrl && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom color="primary">
              ✅ NFC Token Generated Successfully!
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                NFC Authentication URL:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  bgcolor: "white",
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <TextField
                  fullWidth
                  value={nfcTokenUrl}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{ fontFamily: "monospace" }}
                />
                <Button
                  variant="contained"
                  onClick={handleCopyToClipboard}
                  sx={{ minWidth: "100px" }}
                >
                  {copySuccess ? "Copied!" : "Copy"}
                </Button>
              </Box>
              {copySuccess && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ mt: 1, display: "block" }}
                >
                  URL copied to clipboard!
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                This URL can be programmed into an NFC card for tap-to-login
                authentication.
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h4">User Management</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportEmails}
            disabled={users.length === 0}
            sx={{ ml: 2 }}
          >
            Export Emails CSV
          </Button>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="Admin Controls" />
          <Tab label="User Profiles" />
          <Tab label="Pool Pass Holders" />
        </Tabs>

        {activeTab === 0 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Admin Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.name || "N/A"}</TableCell>
                    <TableCell>
                      <Switch
                        checked={user.isAdmin || false}
                        onChange={() =>
                          handleAdminToggle(user.id, user.isAdmin)
                        }
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(user)}
                        title="Delete User"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 1 && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => handleUserSelect(e.target.value)}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedUser && (
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Name"
                  value={userData.name}
                  onChange={(e) =>
                    setUserData({ ...userData, name: e.target.value })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="ID Number"
                  value={userData.idNumber}
                  onChange={(e) =>
                    setUserData({ ...userData, idNumber: e.target.value })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Address"
                  value={userData.address}
                  onChange={(e) =>
                    setUserData({ ...userData, address: e.target.value })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Neighborhood Dollars"
                  type="number"
                  value={userData.neighborhoodDollars}
                  onChange={(e) =>
                    setUserData({
                      ...userData,
                      neighborhoodDollars: Number(e.target.value),
                    })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Profile Photo URL"
                  value={userData.photoURL}
                  onChange={(e) =>
                    setUserData({ ...userData, photoURL: e.target.value })
                  }
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="NFC Card ID"
                  value={userData.nfcCardId}
                  onChange={(e) =>
                    setUserData({ ...userData, nfcCardId: e.target.value })
                  }
                  margin="normal"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 3 }}
                >
                  Update User Profile
                </Button>
              </Box>
            )}
          </>
        )}

        {activeTab === 2 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Pass Type</TableCell>
                  <TableCell>Purchased At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Test User</TableCell>
                  <TableCell>Test Pass</TableCell>
                  <TableCell>Test Date</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm User Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete user{" "}
            <strong>{userToDelete?.email}</strong>?
            <br />
            <br />
            This action will:
            <ul>
              <li>Permanently delete the user account</li>
              <li>Remove all associated NFC tokens</li>
              <li>Cannot be undone</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            autoFocus
          >
            {deleteLoading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default UserManagement;
